/** Case package import (Phase 2 P2-4); trusted systems and external references are in trust-en.ts. */
export default {
  imports: {
    open: 'Import package',
    title: 'Import a case package',
    intro:
      'A case package is a signed file another system exported. It is checked first; nothing is written until you import it.',
    file: 'Package file (.pcp.zip)',
    upload: 'Upload and check',
    uploading: 'Uploading… {{percent}}%',
    checking: 'Checking the package…',
    committing: 'Importing…',
    recent: 'Recent imports',
    resume: 'Show',
    none: 'No imports yet.',
    reportTitle: 'Check report',
    commit: 'Import into this workspace',
    acknowledge: 'Close this import',
    recheck: 'Check again',
    retry: 'Try again',
    findingsTruncated: 'Only the first {{count}} findings are shown.',
    fields: {
      attributes: 'submission details',
      profileRef: 'plan type',
      declaredSrid: 'coordinate system',
      applicant_name: 'applicant name',
      applicant_firm: 'firm',
      applicant_email: 'email address',
      applicant_professionalRegNo: 'professional registration number',
      state: 'submission state',
    },
    committed: 'The package was imported.',
    closed: 'Nothing was imported: this workspace already holds what the package carries.',
    openCase: 'Open the submission',
    close: 'Close',
    stepOk: 'passed',
    stepFailed: 'failed',
    head: 'History up to event {{seq}}',
    counts: '{{events}} events, {{documents}} documents',
    sender: {
      verified: 'Signed by {{system}}.',
      unsigned: 'The package is not signed.',
      unknown_kid: 'Signed with a key none of your trusted systems publishes.',
      invalid: 'The signature is not valid.',
    },
    status: {
      uploading: 'Waiting for the upload…',
      queued: 'Waiting to start…',
      validating: 'Checking the package…',
      validated: 'Checked.',
      invalid: 'This package cannot be imported.',
      committing: 'Importing…',
      committed: 'Imported.',
      closed: 'Closed.',
      failed: 'The import stopped.',
      expired: 'The uploaded package has been deleted.',
    },
    steps: {
      unzip: 'Unpacking',
      bagit: 'Files and digests',
      profile: 'Package version',
      schemas: 'Contents',
      chain: 'History chain',
      boundary: 'Hand-off point',
      seal: 'Signature',
      crs: 'Site coordinates',
      decide: 'Against this workspace',
    },
    findings: {
      outcome_new: 'A new submission: it is not in this workspace yet.',
      outcome_fast_forward: 'It continues the submission held here.',
      outcome_duplicate: 'This package was imported before.',
      outcome_stale: 'This workspace already holds everything the package carries.',
      outcome_conflict: 'The package and this workspace have each moved on, so it cannot be imported.',
      outcome_authority_violation: 'Another system holds this submission, so this sender cannot update it.',
      package_too_large: 'The package is larger than 2 GB.',
      zip_unreadable: 'The file is not a readable zip.',
      unexpected_entry: 'The package holds a file outside the case package layout.',
      unsafe_entry_name: 'The package holds a file name that is not allowed.',
      compression_ratio: 'A file in the package expands far beyond its packed size.',
      digest_mismatch: 'A file does not match the digest the package lists for it.',
      profile_not_importable: 'Public packages carry no personal data or documents and cannot be imported.',
      procedure_not_runnable: 'This workspace does not run the procedure the package was made with.',
      procedure_not_runnable_pack_not_runnable:
        'This workspace does not run the package’s procedure yet; import it again after this workspace is updated to run it.',
      chain_broken: 'The history chain does not verify.',
      boundary_not_reached: 'The package has not reached the hand-off point it names.',
      seal_verified: 'The signature verifies.',
      seal_missing: 'The package is not signed.',
      seal_unknown_kid: 'The signing key is not one your trusted systems publish.',
      seal_invalid: 'The signature does not verify.',
      seal_required: 'The system holding this submission requires signed packages.',
      seal_required_tenant:
        'This workspace refuses packages not signed by one of its trusted systems. Ask an admin to add the sending system under Settings → Planning → Trusted systems, or to turn off “Refuse packages not signed by a trusted system”, then upload the package again.',
      package_id_reused: 'A package with the same id but different contents was imported before.',
      local_case_deleted: 'The submission was deleted here.',
      unknown_members_kept: 'Fields this workspace does not know are kept with the submission.',
      external_id_taken: 'An external reference in the package is linked here to another submission.',
      event_time_not_storable: 'An event time is written in a form this workspace cannot store.',
      chain_not_storable:
        'The history holds a value this workspace would have to hide, so it would no longer verify here.',
      transition_inconsistent: 'The history moves the submission to a stage its procedure does not allow from there.',
      document_id_taken: 'A document in the package has the id of a document of another submission here.',
      document_not_storable:
        'The package holds an original document this workspace cannot store: only drawings and the document types this submission’s pack version declares, each with an id, can be imported.',
      attributes_not_storable: 'The submission details in the package do not fit this workspace’s form ({{field}}).',
      key_not_storable: 'The package holds a field name this workspace cannot store.',
      unknown_member_key_invalid: 'The package holds an unknown field whose name cannot be kept.',
      event_path_invalid: 'An event file is not named the way a case package names it.',
      // detail.type only in a bounded journal-type shape, else detail.typeDigest (P2-4a D-39): never interpolated.
      unknown_event_type: 'Event {{seq}} has a type this workspace does not know; it is kept as it is.',
      case_urn_mismatch: 'The case reference in case.json does not name the case it describes.',
      document_mismatch: 'A drawing is listed with a size other than the size of the file in the package.',
      native_event_seq_invalid: 'A file from another system is tied to an event the package does not hold.',
      too_many_entries: 'The package holds more than 20,000 files.',
      total_too_large: 'The package unpacks to more than 4 GB.',
      entry_too_large: 'A file in the package is larger than 1 GB.',
      entry_unreadable: 'A file in the package cannot be read.',
      small_file_too_large: 'A data file of the package is larger than 16 MB.',
      small_total_too_large: 'The data files of the package are larger than 256 MB together.',
      duplicate_entry: 'The package holds the same file name twice.',
      encrypted_entry: 'A file in the package is encrypted.',
      symlink_entry: 'The package holds a link to another file.',
      unsupported_compression: 'A file in the package is compressed in a way that cannot be read.',
      bagit_txt_invalid: 'The package has no valid bagit.txt.',
      manifest_missing: 'The package has no list of its files and digests.',
      manifest_invalid: 'The list of files and digests cannot be read.',
      manifest_path_invalid: 'The list of files names a file outside the package data.',
      manifest_entry_missing: 'A file the package lists is not in it.',
      unlisted_payload: 'The package holds a file it does not list.',
      tag_manifest_invalid: 'The list of signed files cannot be read.',
      tag_manifest_coverage: 'The list of signed files does not cover exactly the files it must.',
      tag_file_missing: 'A signed file is missing from the package.',
      tag_digest_mismatch: 'A signed file does not match its digest.',
      bag_info_missing: 'The package has no bag-info.txt.',
      bag_info_malformed: 'bag-info.txt cannot be read.',
      bag_info_missing_label: 'bag-info.txt has no {{label}}.',
      bag_info_duplicate: 'bag-info.txt names {{label}} twice.',
      bag_info_invalid: 'bag-info.txt has a value for {{label}} that is not valid.',
      payload_oxum_mismatch: 'The file count and size bag-info.txt states do not match the package.',
      profile_unsupported: 'This workspace reads case packages of version {{supported}} only.',
      upcast_failed: 'The package could not be brought up to the current version.',
      case_json_missing: 'The package has no case.json.',
      case_json_unreadable: 'case.json cannot be read.',
      case_schema_unsupported: 'case.json is not of the version the package names.',
      case_json_invalid: 'case.json has a field that is not valid ({{field}}).',
      extensions_invalid: 'case.json holds data from other systems in a form that cannot be kept.',
      unknown_members_too_large: 'The fields this workspace does not know are too large to keep with the submission.',
      privacy_path_invalid: 'case.json lists a privacy rule that cannot be read.',
      privacy_mismatch: 'The privacy rules in case.json do not match their digest.',
      bag_info_mismatch: 'bag-info.txt and case.json disagree ({{label}}).',
      public_case_missing: 'The package has no case.public.json.',
      public_case_invalid:
        'case.public.json is not valid, holds personal data, or describes another case or point in its history.',
      pack_mismatch: 'This workspace runs the procedure with other privacy rules than the package.',
      documents_unexpected: 'The package carries a drawing list its kind of package never carries.',
      documents_index_missing: 'The package has no drawing list, although its history names drawings.',
      documents_index_invalid: 'The drawing list cannot be read.',
      document_unlisted: 'A drawing in the list is not among the package files with its digest.',
      document_type_unsupported: 'A drawing of revision {{revision}} is of a kind this workspace cannot store.',
      document_missing: 'A drawing the history names at event {{seq}} is not in the package.',
      too_many_native_files: 'The package holds more than 1,000 files from other systems.',
      native_path_invalid: 'A file from another system is not where case.json says it is.',
      native_duplicate: 'case.json lists a file from another system twice.',
      native_mismatch: 'A file from another system does not match the digest or size case.json lists.',
      native_unlisted: 'The package holds a file from another system that case.json does not list.',
      too_many_events: 'The package holds more than 10,000 events.',
      event_unreadable: 'An event file cannot be read.',
      event_invalid: 'An event file is not a valid case package event.',
      event_not_canonical: 'An event file is not written in the exact form a case package writes it.',
      event_wrong_case: 'An event file belongs to another case.',
      event_seq_mismatch: 'An event file holds an event other than the one its name says.',
      events_incomplete: 'The package holds {{found}} of the {{expected}} events of its history.',
      external_ids_mismatch: 'The external references in case.json are not the ones its history links.',
      chain_unverifiable: 'The history chain cannot be checked.',
      head_mismatch: 'The history ends at another event than case.json says.',
      replay_mismatch: 'Replaying the history does not give the submission case.json describes.',
      based_on_off_chain: 'The point the package says it was built on is not in its history.',
      verified_head_off_chain: 'The last verified point case.json names is not in its history.',
      transition_unknown: 'Event {{seq}} records a step the procedure here cannot take.',
      event_type_mismatch: 'Event {{seq}} is labelled with a type its step does not have here.',
      clock_event_invalid: 'Event {{seq}} records a deadline change the procedure here could not have made.',
      link_event_invalid: 'Event {{seq}} records a link to a parent submission the procedure here could not have made.',
      stage_unknown: 'The history leaves the submission at a stage the procedure here does not have.',
      landing: 'The submission lands at stage {{stage}}.',
      seal_coverage: 'The signature does not cover exactly the files it must.',
      seal_head_mismatch: 'The signature names another point in the history than the package holds.',
      crs_unknown: 'The site is in a coordinate system this workspace does not know.',
      crs_mismatch: 'The site is in EPSG:{{srid}}, while the submission declares EPSG:{{declaredSrid}}.',
      crs_not_offered: 'The site is in EPSG:{{srid}}, which this workspace does not offer.',
      crs_out_of_range: 'The site has coordinates outside its coordinate system.',
      place_out_of_range: 'The site has a position outside the range of longitude and latitude.',
      place_inconsistent:
        'The site carries a longitude and latitude copy where it should not, or lacks one where it should.',
      ring_not_closed: 'The site boundary is not closed.',
      geometry_too_large: 'The site has too many points ({{vertices}}; at most 200,000).',
      geopackage_invalid: 'site.gpkg is not a GeoPackage.',
      extension_namespace_taken:
        'The package already holds data under {{namespace}}, the name its unknown fields would be kept under.',
      unknown: 'Finding: {{code}}',
    },
    errors: {
      IMPORT_FAILED: 'Something went wrong during the import. Try again.',
      IMPORT_PACKAGE_MISSING: 'The uploaded package could not be read. Upload it again.',
      IMPORT_PACKAGE_CHANGED: 'The stored package changed after it was checked. Upload it again.',
      IMPORT_OUTCOME_CHANGED:
        'Something changed since the package was checked (the submission, the trusted systems or the import rules). Upload it again to check it.',
      IMPORT_DOCUMENT_FAILED: 'A file of the package could not be stored. Try again.',
      IMPORT_EXTENSIONS_TOO_LARGE:
        'The case would keep more data from other systems than a case package can carry (1 MiB), so it was not imported.',
      IMPORT_STALE: 'The import stopped unexpectedly. Try again.',
    },
  },
  errors: {
    too_many_imports: 'You already have three case packages being checked or imported. Wait for one to finish.',
    invalid_import: 'Those import details are not valid.',
    import_not_found: 'That import was not found.',
    import_not_uploaded: 'The package has not been uploaded yet.',
    import_not_ready: 'That import cannot do this in its current state.',
    import_outdated: 'The submission changed since this package was checked. Check it again.',
    import_closed: 'That import is closed.',
    import_expired: 'The uploaded package has been deleted. Upload it again.',
    stale_base: 'This package does not continue the submission as it stands here, so nothing was imported.',
    package_id_reused: 'A package with this id was already imported with different contents.',
    recipient_not_trusted: 'The receiving system must be one of your trusted systems (Settings → Planning).',
    external_id_taken: 'That external reference is already linked to another submission.',
    invalid_external_id: 'That external reference is not valid.',
    external_id_issuer_unset: "Set the council's PBT code in Settings → Planning, or name who issued the id.",
    external_id_not_linked: 'That external reference is not linked to this submission.',
    invalid_mirror_mark: 'Those checklist marks are not valid.',
    mirror_item_unknown: 'That checklist item is no longer in the bundle. Prepare the bundle again.',
    mirror_not_available: 'This submission has no OSC bundle.',
    invalid_trusted_system: 'The trusted system is not valid.',
    trusted_system_not_found: 'That trusted system was not found.',
    trusted_system_holds_cases: 'This system still holds submissions forwarded to it. Recall them first.',
  },
  events: {
    case_external_id_linked: 'External reference linked',
    case_external_id_unlinked: 'External reference unlinked',
    case_foreign_event_recorded: 'Message from another system recorded',
  },
} as const
